import { type ConversationSummary } from 'entities/WebMcp/lib/serializers'

export interface UnreadResult {
  total: number
  scope: { inbox: string; only_this_inbox: true }
  results: ConversationSummary[]
}

export const UNREAD_SCOPE_FALLBACK = 'the inbox the user is viewing'
export const EMPTY_UNREAD_SCOPE_GUIDANCE =
  'If results is empty, say there are no unread conversations in the scope.inbox field in the data below only, name that inbox from the data, and tell the user there may be unread messages in their other inboxes. You cannot switch inbox yourself, so ask them to switch it in the app if they want you to look elsewhere.'

/**
 * Build the `list_unread_conversations` payload.
 *
 * The underlying request is scoped to one inbox, so an empty list is a dead end
 * rather than an answer. Observed 2026-08-06: the same tool returned 73 unread
 * conversations from one inbox and none from another, and the agent reported the
 * empty case as "there are no unread conversations at the moment" — true of that
 * inbox, and read by the user as true of their account.
 *
 * The scope travels with the data rather than trusting the model to infer it from
 * a bare count. The empty-result guidance is exported separately so the trusted
 * instruction can refer to data.scope.inbox without interpolating a
 * workspace-member-controlled inbox name into trusted text. It must not offer to
 * switch inbox, because no tool can do that: promising it would claim a capability
 * the agent lacks, which Article II forbids. If inbox tools are ever added, that
 * guidance and its test both have to change, and the test will fail until they do.
 */
export const buildUnreadResult = (
  results: ConversationSummary[],
  total: number,
  inboxName?: string | null
): UnreadResult => {
  const inbox = inboxName?.trim() || UNREAD_SCOPE_FALLBACK
  return {
    total,
    scope: { inbox, only_this_inbox: true },
    results,
  }
}
